import { useRef, useEffect } from 'react'
import { object, mixed, number } from 'yup'
import { useClient } from 'jsonapi-react'

const TYPES = ['image/png', 'image/jpg', 'image/jpeg']
const MAX_SIZE = 10000000

export default function usePhotoUpload(inputs = []) {
  const ref = useRef(null)
  const client = useClient()

  useEffect(() => {
    const input = document.createElement('input')

    input.type = 'file'
    input.style.display = 'none'

    document.body.appendChild(input)

    ref.current = input

    return () => {
      document.body.removeChild(ref.current)
    }
  }, inputs)

  const choose = (callback) => {
    if (!ref.current) {
      return null
    }

    async function onChange({ target }) {
      const file = target.files[0]
      target.value = ''

      try {
        const schema = object().shape({
          type: mixed()
            .required()
            .oneOf(TYPES, 'File type must be jpg or png'),
          size: number()
            .required()
            .max(MAX_SIZE, 'File size must be less than 10MB'),
        })

        await schema.validate({
          type: file.type,
          size: file.size,
        })

        const reader = new FileReader()

        reader.addEventListener('load', () => {
          callback({
            file,
            url: reader.result,
          })
        }, false)

        reader.readAsDataURL(file)
      } catch (error) {
        callback({ error })
      }
    }

    ref.current.addEventListener('change', onChange, {
      capture: false,
      once: true,
      passive: true,
    })

    ref.current.click()
  }

  const save = async (file, data, token) => {
    const photo = await client.mutate(['photos', { token }], data, {
      invalidate: null,
    })

    if (!photo.data) {
      return photo
    }

    const formData = new FormData()
    formData.append('file', file, file.name)

    const result = await client.mutate(
      ['photos', photo.data.id, 'upload'],
      undefined,
      {
        body: formData,
        headers: { 'Content-Type': null }
      },
    )

    if (!result.data) {
      client.delete(['photos', photo.data.id])
    }

    return result
  }

  const deletePhoto = id => {
    return client.delete(['photos', id])
  }

  return {
    choose,
    save,
    delete: deletePhoto,
  }
}
